import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { MapPin, BedDouble, Bath, Car, Ruler, BadgeCheck, ArrowLeft, ArrowRight, Loader2, Building2, Check, Heart, Pencil, Images, ChevronDown, MoreHorizontal, Eye, Archive, ShieldCheck, Share2 } from "lucide-react";
import { ShareDialog } from "@/components/ShareDialog";
import { listingShareUrl, publicSiteUrl, warmSharePreview, listingImageMeta } from "@/lib/share";
import { PublicHeader } from "@/components/PublicHeader";
import { amenityLabel, propertyTypeLabel, saleTypeShortLabel } from "@/lib/taxonomy";
import { Navbar } from "@/components/Navbar";
import { api, priceLabel, apiError } from "@/lib/api";
import { trackEvent } from "@/lib/track";
import { proProfilePath, trackProfileClick } from "@/lib/proProfile";
import { useAuth } from "@/context/AuthContext";
import { InquiryDialog } from "@/components/InquiryDialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ListingCommercialDetails } from "@/components/ListingCommercialDetails";
import { ListingGallery } from "@/components/ListingGallery";
import { Seo } from "@/components/Seo";

const OWNER_STATUSES = ["Active", "Under Negotiation", "Reserved", "Sold", "Leased", "Unavailable"];

// SEO P1.1: concise, natural listing description built ONLY from real public fields.
// Prefers the listing's own description when substantial; never invents attributes.
function buildListingDescription(p) {
  const prefix = p.listing_type === "For Rent" ? "For Rent" : (p.listing_type === "Unit Hosting" ? "Stay" : "For Sale");
  const raw = (p.description || "").replace(/\s+/g, " ").trim();
  if (raw.length >= 60) return raw.length > 180 ? raw.slice(0, 177).trim() + "…" : raw;
  const specs = [];
  if (p.bedrooms) specs.push(`${p.bedrooms}BR`);
  if (p.bathrooms) specs.push(`${p.bathrooms}BA`);
  if (Number(p.floor_area_sqm) > 0) specs.push(`${p.floor_area_sqm} sqm`);
  const head = [specs.join(" "), propertyTypeLabel(p.property_type) || "property"].filter(Boolean).join(" ");
  const loc = [p.project_name, p.city].filter(Boolean).join(", ");
  return `${prefix}: ${head}${loc ? " in " + loc : ""} — ${priceLabel(p)}. View full details on PropertyConnect.`
    .replace(/\s+/g, " ").trim();
}

// SEO P1.1: accurate Schema.org JSON-LD (mirrors backend OG parity). Dwellings ->
// Accommodation subtype; non-dwellings -> Product. Offer only when a real price exists.
function buildListingJsonLd(p, id) {
  const canonical = listingShareUrl(id);
  const site = publicSiteUrl();
  const lt = p.listing_type || "For Sale";
  const pt = (p.property_type || "").trim();
  const dwelling = { Condominium: "Apartment", Apartment: "Apartment", Studio: "Apartment", "House and Lot": "House", House: "House", Townhouse: "House", Villa: "House" };
  const nodeType = dwelling[pt];
  const city = (p.city || "").replace(/\s+/g, " ").trim();
  let name = (p.title || "").replace(/\s+/g, " ").trim();
  if (!name) name = [p.bedrooms ? `${p.bedrooms}BR` : "", pt || "Property", city ? `in ${city}` : ""].filter(Boolean).join(" ") || "Property Listing";
  let img = p.image || (p.photo_urls && p.photo_urls[0] && p.photo_urls[0].url) || "";
  if (!/^https?:\/\//.test(img)) img = "";
  const node = { "@context": "https://schema.org", "@type": nodeType || "Product", name, description: buildListingDescription(p), url: canonical };
  if (img) node.image = [img];
  const price = lt === "Unit Hosting" ? Number(p.nightly_rate || 0) : Number(p.price || 0);
  if (price > 0) {
    node.offers = {
      "@type": "Offer", price: Math.round(price * 100) / 100, priceCurrency: "PHP",
      availability: "https://schema.org/InStock", url: canonical,
      businessFunction: lt === "For Sale" ? "http://purl.org/goodrelations/v1#Sell" : "http://purl.org/goodrelations/v1#LeaseOut",
    };
  }
  if (nodeType) {
    if (Number(p.bedrooms) > 0) node.numberOfBedrooms = Number(p.bedrooms);
    if (Number(p.bathrooms) > 0) node.numberOfBathroomsTotal = Number(p.bathrooms);
    if (Number(p.floor_area_sqm) > 0) node.floorSize = { "@type": "QuantitativeValue", value: Number(p.floor_area_sqm), unitCode: "MTK" };
  } else if (pt) {
    node.category = pt;
  }
  if (city) node.address = { "@type": "PostalAddress", addressLocality: city, addressCountry: "PH" };
  const breadcrumb = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Marketplace", item: `${site}/marketplace` },
      { "@type": "ListItem", position: 2, name, item: canonical },
    ],
  };
  return [node, breadcrumb];
}

function SpecRow({ label, value, suffix }) {
  if (value === undefined || value === null || value === "" || value === 0) return null;
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value}{suffix || ""}</span>
    </div>
  );
}

export default function PublicListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isConsumer = user && (user.account_type || "PROFESSIONAL") === "CONSUMER";
  const isPro = user && (user.account_type || "PROFESSIONAL") !== "CONSUMER";
  const showSave = !isPro;
  const [p, setP] = useState(undefined); // undefined = loading, null = not found
  const [saved, setSaved] = useState(false);
  const [inqOpen, setInqOpen] = useState(false);
  const [inqMode, setInqMode] = useState("inquiry");
  const [previewMode, setPreviewMode] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [pending, setPending] = useState(null); // { kind:'status'|'archive', status, title, body }
  const [shareOpen, setShareOpen] = useState(false);

  const isOwner = !!p?.is_owner;

  useEffect(() => {
    if (!p) return;
    const prevTitle = document.title;
    const prefix = p.listing_type === "For Rent" ? "For Rent" : (p.listing_type === "Unit Hosting" ? "Stay" : "For Sale");
    const title = `${prefix} — ${p.title}`;
    document.title = `${title} · PropertyConnect`;
    const set = (key, val, prop = true) => {
      if (!val) return;
      const a = prop ? "property" : "name";
      let el = document.head.querySelector(`meta[${a}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(a, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    let img = p.image || (p.photo_urls && p.photo_urls[0]) || "";
    if (!/^https?:\/\//.test(img)) img = `${publicSiteUrl()}/brand/pc-appicon-512.png`;
    const desc = buildListingDescription(p);
    const url = listingShareUrl(id);
    set("description", desc, false);
    set("og:site_name", "PropertyConnect");
    set("og:title", title);
    set("og:description", desc);
    set("og:image", img);
    const isListingPhoto = !img.endsWith("/brand/pc-appicon-512.png");
    const meta = isListingPhoto ? listingImageMeta(p, img) : {};
    set("og:image:secure_url", img.startsWith("https://") ? img : "");
    set("og:image:type", meta.type);
    set("og:image:width", meta.width && String(meta.width));
    set("og:image:height", meta.height && String(meta.height));
    set("og:image:alt", p.title);
    set("og:type", "product");
    set("og:url", url);
    set("twitter:card", "summary_large_image", false);
    set("twitter:title", title, false);
    set("twitter:description", desc, false);
    set("twitter:image", img, false);
    let canon = document.head.querySelector('link[rel="canonical"]');
    const hadCanon = !!canon; const prevCanon = canon && canon.getAttribute("href");
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", url);
    if (isListingPhoto) warmSharePreview(url);
    return () => {
      ["og:image:secure_url", "og:image:type", "og:image:width", "og:image:height", "og:image:alt"]
        .forEach((k) => document.head.querySelector(`meta[property="${k}"]`)?.remove());
      document.title = prevTitle; if (hadCanon) canon.setAttribute("href", prevCanon || ""); else canon.remove(); };
  }, [p, id]);

  const applyStatus = async (status) => {
    setStatusBusy(true);
    try {
      await api.patch(`/properties/${id}/status`, { status });
      toast.success(`Listing marked ${status}`);
      if (status === "Active") { const r = await api.get(`/public/listings/${id}`); setP(r.data); }
      else nav("/my-properties"); // non-Active leaves the public marketplace
    } catch (e) { toast.error(apiError(e)); }
    finally { setStatusBusy(false); }
  };

  const requestStatus = (status) => {
    if (status === "Sold" || status === "Leased") {
      setPending({ kind: "status", status,
        title: `Mark this listing as ${status}?`,
        body: `Marking this listing as ${status} will remove it from active matching and marketplace discovery. Continue?` });
    } else { applyStatus(status); }
  };

  const requestArchive = () => setPending({ kind: "archive",
    title: "Archive this listing?",
    body: "Archiving removes this listing from the marketplace and active matching. This is a soft-archive — the listing and its historical inquiries and collaborations are preserved and can be reviewed later. Continue?" });

  const confirmPending = async () => {
    const act = pending; setPending(null);
    if (!act) return;
    if (act.kind === "archive") {
      try { await api.delete(`/properties/${id}`); toast.success("Listing archived"); nav("/my-properties"); }
      catch (e) { toast.error(apiError(e)); }
    } else { applyStatus(act.status); }
  };


  useEffect(() => {
    api.get(`/public/listings/${id}`).then((r) => setP(r.data)).catch(() => setP(null));
    if (id) trackEvent("listing_viewed", { entity_type: "listing", entity_id: id });
  }, [id]);

  useEffect(() => {
    if (!isConsumer) { setSaved(false); return; }
    api.get("/me/saved-properties").then((r) => setSaved(r.data.some((d) => d.id === id))).catch(() => {});
  }, [id, isConsumer]);

  const toggleSave = async () => {
    if (!user) { nav("/login"); return; }
    if (!isConsumer) return;
    try {
      if (saved) { await api.delete(`/me/saved-properties/${id}`); setSaved(false); toast.success("Removed from saved"); }
      else { await api.post("/me/saved-properties", { property_id: id }); setSaved(true); toast.success("Saved property"); }
    } catch (e) { toast.error(apiError(e)); }
  };

  if (p === undefined) {
    return (
      <div className="min-h-screen bg-slate-50">{user ? <Navbar /> : <PublicHeader />}
        <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      </div>
    );
  }
  if (p === null) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="listing-detail-notfound">{user ? <Navbar /> : <PublicHeader />}
        <Seo robots="noindex,follow" />
        <div className="text-center py-32 px-4">
          <Building2 className="h-10 w-10 text-slate-300 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-3">Listing not available</h1>
          <p className="text-slate-500 mt-2 text-sm">This property may no longer be published on the public marketplace.</p>
          <Link to="/marketplace"><Button className="mt-5 bg-[#0F2C59] text-white rounded-xl">Back to marketplace</Button></Link>
        </div>
      </div>
    );
  }

  const photos = p.photo_urls?.length ? p.photo_urls : [{ id: "cover", url: p.image }];
  const rent = p.listing_type === "For Rent";
  const hosting = p.listing_type === "Unit Hosting";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="listing-detail-page">
      <Seo robots="index,follow" jsonLd={buildListingJsonLd(p, id)} />
      {user ? <Navbar /> : <PublicHeader />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <Link to="/marketplace" data-testid="back-to-marketplace" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F2C59]"><ArrowLeft className="h-4 w-4" /> Back to marketplace</Link>
          <Button data-testid="public-share-btn" size="sm" variant="outline" onClick={() => setShareOpen(true)} className="rounded-lg border-[#0F2C59] text-[#0F2C59] font-semibold shrink-0"><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
        </div>

        {isOwner && previewMode && (
          <div data-testid="owner-preview-banner" className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-[#C79A3B]/40 bg-[#C79A3B]/10 px-4 py-2.5">
            <span className="text-sm text-slate-700 flex items-center gap-2"><Eye className="h-4 w-4 text-[#C79A3B]" /> Previewing as a visitor — owner controls hidden.</span>
            <Button data-testid="exit-preview-btn" size="sm" variant="outline" onClick={() => setPreviewMode(false)} className="rounded-lg border-[#0F2C59] text-[#0F2C59]">Exit preview</Button>
          </div>
        )}

        {isOwner && !previewMode && (
          <div data-testid="owner-toolbar" className="mb-4 rounded-xl border border-[#0F2C59]/15 bg-white p-3 sm:p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2C59] mr-1"><ShieldCheck className="h-4 w-4 text-[#C79A3B]" /> You manage this listing</span>
              <Button data-testid="owner-edit-btn" size="sm" onClick={() => nav(`/have-property?edit=${id}`)} className="rounded-lg bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white font-semibold"><Pencil className="h-4 w-4 mr-1.5" /> Edit Listing</Button>
              <Button data-testid="owner-photos-btn" size="sm" variant="outline" onClick={() => nav(`/property/${id}`)} className="rounded-lg border-[#0F2C59] text-[#0F2C59] font-semibold"><Images className="h-4 w-4 mr-1.5" /> Manage Photos</Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="owner-status-trigger" size="sm" variant="outline" disabled={statusBusy} className="rounded-lg border-[#0F2C59] text-[#0F2C59] font-semibold">
                    {statusBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Change Status <ChevronDown className="h-4 w-4 ml-1.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>Set listing status</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {OWNER_STATUSES.map((s) => (
                    <DropdownMenuItem key={s} data-testid={`owner-status-${s.replace(/\s/g, "-")}`} onClick={() => requestStatus(s)}>Mark {s}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="owner-more-trigger" size="sm" variant="ghost" className="rounded-lg text-slate-600"><MoreHorizontal className="h-4 w-4 mr-1.5" /> More <ChevronDown className="h-4 w-4 ml-1" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid="owner-preview-btn" onClick={() => setPreviewMode(true)} className="gap-2"><Eye className="h-4 w-4" /> Preview as Visitor</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="owner-archive-btn" onClick={requestArchive} className="gap-2 text-red-600"><Archive className="h-4 w-4" /> Archive Listing</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Gallery + description */}
          <div className="lg:col-span-2 min-w-0">
            <ListingGallery photos={photos} alt={p.title} />

            {p.description && (
              <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5">
                <h2 className="font-display text-lg font-bold text-slate-900">About this property</h2>
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-line leading-relaxed">{p.description}</p>
              </div>
            )}

            {(p.amenities?.length > 0 || p.features?.length > 0) && (
              <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-5">
                <h2 className="font-display text-lg font-bold text-slate-900">Features & amenities</h2>
                <div className="flex flex-wrap gap-2 mt-3">
                  {[...new Map([...(p.features || []), ...(p.amenities || [])].filter(a => typeof a === "string" && a.trim()).map(a => { const lbl = amenityLabel(a.trim()); return [lbl.toLowerCase(), lbl]; })).values()].map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full"><Check className="h-3 w-3 text-[#0F2C59]" /> {a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 lg:sticky lg:top-20">
              <div className="flex items-center gap-1.5">
                <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${hosting ? "bg-emerald-600 text-white" : rent ? "bg-[#0F2C59] text-white" : "bg-[#C79A3B] text-white"}`}>{hosting ? "Stay" : p.listing_type}</span>
                {!rent && !hosting && p.sale_type && (
                  <span data-testid="listing-detail-sale-type" className="text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{saleTypeShortLabel(p.sale_type)}</span>
                )}
              </div>
              <div className="font-display text-3xl font-bold text-slate-900 mt-3" data-testid="listing-price">{hosting ? `₱${(p.nightly_rate || 0).toLocaleString()} / night` : priceLabel(p)}</div>
              <h1 className="text-base font-semibold text-slate-800 mt-1">{p.title}</h1>
              <p className="text-sm text-slate-500 mt-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.city}</p>
              {(p.updated_at || p.created_at) && (
                <p className="text-xs text-slate-400 mt-1" data-testid="listing-freshness">Updated {new Date(p.updated_at || p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>
              )}
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-1">{propertyTypeLabel(p.property_type)}</p>

              <ListingCommercialDetails p={p} />

              {hosting && (
                <div className="mt-3 space-y-1.5 text-sm text-slate-700" data-testid="hosting-detail">
                  <div>Up to <b>{p.max_guests}</b> guests · {p.beds > 0 && <><b>{p.beds}</b> bed(s) · </>}{p.bedrooms} BR · {p.bathrooms} BA</div>
                  <div>Minimum stay: <b>{p.minimum_stay_nights}</b> night(s)</div>
                  <div>Availability: <b>{(p.availability_status || "AVAILABLE").replace(/_/g, " ").toLowerCase()}</b></div>
                  {(p.check_in_time || p.check_out_time) && <div>Check-in {p.check_in_time || "—"} · Check-out {p.check_out_time || "—"}</div>}
                  {p.house_rules && <div className="text-slate-500 text-xs pt-1">House rules: {p.house_rules}</div>}
                  <div className="text-xs text-slate-400 pt-1">Availability must be confirmed by the host or property manager.</div>
                </div>
              )}

              {showSave && (
                <Button
                  data-testid="detail-save-toggle"
                  aria-pressed={saved}
                  onClick={toggleSave}
                  variant={saved ? "default" : "outline"}
                  className={`w-full mt-4 h-11 rounded-xl font-semibold ${saved ? "bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white" : "border-[#0F2C59] text-[#0F2C59]"}`}
                >
                  <Heart className={`h-4 w-4 mr-2 ${saved ? "fill-current" : ""}`} /> {saved ? "Saved" : "Save property"}
                </Button>
              )}

              <div className="grid grid-cols-2 gap-2 mt-3">
                {hosting ? (
                  <Button data-testid="open-availability" onClick={() => { setInqMode("availability"); setInqOpen(true); }} className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">Check Availability</Button>
                ) : (
                  <Button data-testid="open-viewing" onClick={() => { setInqMode("viewing"); setInqOpen(true); }} variant="outline" className="h-11 rounded-xl border-[#0F2C59] text-[#0F2C59] font-semibold">Request Viewing</Button>
                )}
                <Button data-testid="open-inquiry" onClick={() => { setInqMode("inquiry"); setInqOpen(true); }} variant="outline" className="h-11 rounded-xl border-[#0F2C59] text-[#0F2C59] font-semibold">Send Inquiry</Button>
              </div>

              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-slate-100">
                {p.bedrooms > 0 && <span className="inline-flex items-center gap-1 text-sm text-slate-600"><BedDouble className="h-4 w-4 text-slate-400" /> {p.bedrooms} Bed{p.bedrooms > 1 ? "s" : ""}</span>}
                {p.bathrooms > 0 && <span className="inline-flex items-center gap-1 text-sm text-slate-600"><Bath className="h-4 w-4 text-slate-400" /> {p.bathrooms} Bath{p.bathrooms > 1 ? "s" : ""}</span>}
                {p.parking_slots > 0 && <span className="inline-flex items-center gap-1 text-sm text-slate-600"><Car className="h-4 w-4 text-slate-400" /> {p.parking_slots} Parking</span>}
                {p.floor_area_sqm > 0 && <span className="inline-flex items-center gap-1 text-sm text-slate-600"><Ruler className="h-4 w-4 text-slate-400" /> {p.floor_area_sqm} sqm</span>}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <SpecRow label="Floor area" value={p.floor_area_sqm} suffix=" sqm" />
                <SpecRow label="Lot area" value={p.lot_area_sqm} suffix=" sqm" />
                <SpecRow label="Furnishing" value={p.furnishing} />
                <SpecRow label="Completion Status" value={p.completion_status} />
                <SpecRow label="Neighborhood" value={p.neighborhood} />
              </div>

              {p.owner && (() => {
                const path = proProfilePath(p.owner);
                const head = (
                  <div className="flex items-center gap-3">
                    <img src={p.owner.avatar} alt="" className="h-10 w-10 rounded-full bg-slate-100" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1">
                        {p.owner.name}
                        {p.owner.verification_status === "PROFESSIONAL_VERIFIED" && <BadgeCheck className="h-4 w-4 text-success" title="Verified professional" />}
                      </p>
                      <p className="text-xs text-slate-500 truncate">{p.owner.role_label}{p.owner.organization_name ? ` · ${p.owner.organization_name}` : ""}</p>
                    </div>
                  </div>
                );
                return (
                  <div className="mt-4 pt-4 border-t border-slate-100" data-testid="listing-represented-by">
                    <p className="text-xs font-semibold text-slate-500 mb-2">Represented by</p>
                    {path ? (
                      <Link to={path} data-testid="listing-pro-profile-link" onClick={() => trackProfileClick(p.owner, "property_detail")}
                        className="group block rounded-lg -m-1 p-1 hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B]/50">
                        {head}
                        <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#C79A3B] group-hover:gap-1.5 transition-all">View Professional Profile <ArrowRight className="h-3.5 w-3.5" /></span>
                      </Link>
                    ) : head}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      <InquiryDialog open={inqOpen} onOpenChange={setInqOpen} listingId={id} mode={inqMode} maxGuests={p.max_guests || 0} />

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        url={listingShareUrl(id)}
        entityId={id}
        title={`${p.listing_type === "For Rent" ? "For Rent" : "For Sale"} — ${p.title}`}
        text={`${p.title} — ${priceLabel(p)} · ${p.city}. View this property on PropertyConnect.`}
        subtitle="Share this public PropertyConnect listing with potential buyers and your network."
      />

      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent data-testid="owner-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.title}</AlertDialogTitle>
            <AlertDialogDescription>{pending?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="owner-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="owner-confirm-ok" onClick={confirmPending} className={pending?.kind === "archive" ? "bg-red-600 hover:bg-red-700" : "bg-[#0F2C59] hover:bg-[#0F2C59]/90"}>
              {pending?.kind === "archive" ? "Archive Listing" : `Mark ${pending?.status}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SiteFooter variant={user ? "compact" : "full"} />
    </div>
  );
}
